import * as THREE from 'three';
import { isMobile } from '../utils/device.js';

class FirstPersonController {
  constructor(engine) {
    this.engine = engine;
    this.camera = engine.camera;
    this.controls = engine.controls; // OrbitControls
    this.mobile = isMobile();

    this.isActive = false;

    // Salvataggio stato lucchetto per il ripristino all'uscita
    this._savedLocked = false;

    // Salvataggio stato camera per il ripristino
    this._savedPos = null;
    this._savedTarget = null;
    this._savedQuat = null;

    // Angoli di visuale
    this.yaw = 0; // rotazione orizzontale (Y)
    this.pitch = 0; // rotazione verticale (X)
    this.PITCH_LIMIT = Math.PI / 2.2; // ~81°

    // Movimento
    this.keys = { forward: false, backward: false, left: false, right: false };
    this.MOVE_SPEED = 0.1; // unità/frame (~10 cm a 60 fps)
    this.EYE_HEIGHT = 1.5; // altezza occhi in unità-scena (1 u = 1 metro)

    // Mouse / touch look (solo yaw – niente su/giù)
    this._mouseDown = false;
    this._lastMX = 0;
    this.MOUSE_SENS = 0.0028;

    // Joystick mobile
    this.joystick = { active: false, id: null, cx: 0, cy: 0, dx: 0, dy: 0 };
    this.JOYSTICK_R = 47; // px max-radius
    this._joystickKnob = null;

    // Touch look mobile
    this._lookTouch = null; // { id, x, y }

    // DOM
    this.overlay = null;

    // RAF id
    this._rafId = null;

    // Vettori riutilizzabili
    this._fwd = new THREE.Vector3();
    this._right = new THREE.Vector3();
    this._vel = new THREE.Vector3();
    this._euler = new THREE.Euler(0, 0, 0, 'YXZ');
    this._quat = new THREE.Quaternion();

    // Bind handlers
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._loop = this._loop.bind(this);
  }

  // API pubblica
  enter() {
    if (this.isActive) return;
    this.isActive = true;

    // Salva stato camera
    this._savedPos = this.camera.position.clone();
    this._savedTarget = this.controls.target.clone();
    this._savedQuat = this.camera.quaternion.clone();

    // Disabilita OrbitControls
    this.controls.enabled = false;

    // Posiziona la camera ad altezza occhio, mantenendo la posizione XZ dell'ultima vista
    this.camera.position.set(this._savedPos.x, this.EYE_HEIGHT, this._savedPos.z);

    // Ricava yaw dal fronte della camera, azzera pitch
    // World forward = R_Y(yaw) × (0,0,-1) = (-sin(yaw), 0, -cos(yaw))
    // quindi: yaw = atan2(-dir.x, -dir.z)
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    this.yaw = Math.atan2(-dir.x, -dir.z);
    this.pitch = 0;
    this._applyRotation();

    // UI
    this._hideUI();
    this._buildOverlay();

    // Blocca il progetto per evitare selezioni/modifiche accidentali in FP mode
    // Salva lo stato precedente del lucchetto per ripristinarlo all'uscita
    this._savedLocked = window.app?.isLocked ?? false;
    window.app?.setLocked(true);

    // Mouse look sul canvas – solo yaw (destra/sinistra)
    const canvas = this.engine.renderer.domElement;
    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('mouseleave', this._onMouseUp);

    // Listener tastiera — fase di cattura (capture:true) per bloccare
    // tutti gli altri listener (main.js, input-controller.js, ecc.)
    document.addEventListener('keydown', this._onKeyDown, true);
    document.addEventListener('keyup', this._onKeyUp, true);

    // Avvia loop interno (per movimento continuo con tasti/joystick)
    this._rafId = requestAnimationFrame(this._loop);

    window.requestRender?.();
  }

  exit() {
    if (!this.isActive) return;
    this.isActive = false;

    cancelAnimationFrame(this._rafId);
    this._rafId = null;

    // Ripristina camera e controlli
    this.camera.position.copy(this._savedPos);
    this.camera.quaternion.copy(this._savedQuat);
    this.camera.up.set(0, 1, 0);
    this.controls.target.copy(this._savedTarget);
    this.controls.enabled = true;
    this.controls.update();

    // Rimuovi overlay e mostra UI
    if (this.overlay) { this.overlay.remove(); this.overlay = null; }
    this._showUI();

    // Rimuovi listener mouse
    const canvas = this.engine.renderer.domElement;
    canvas.removeEventListener('mousedown', this._onMouseDown);
    canvas.removeEventListener('mousemove', this._onMouseMove);
    canvas.removeEventListener('mouseup', this._onMouseUp);
    canvas.removeEventListener('mouseleave', this._onMouseUp);

    // Rimuovi listener tastiera
    document.removeEventListener('keydown', this._onKeyDown, true);
    document.removeEventListener('keyup', this._onKeyUp, true);

    // Ripristina lo stato del lucchetto com'era prima di entrare in FP mode
    window.app?.setLocked(this._savedLocked);

    // Reset stato tasti/joystick per la prossima sessione
    this.keys = { forward: false, backward: false, left: false, right: false };
    this.joystick.active = false;

    window.app?.updateAllButtons();
    window.requestRender?.();
  }

  dispose() { this.exit(); }

  // Nasconde / mostra UI
  _hideUI() {
    document.querySelectorAll('#info-panel, .toolbar, #catalog-panel, .panel-toggle-btn, .project-name-label')
      .forEach(el => {
        if (el.style.display === 'none') return; // già nascosto
        el.dataset.fpDisplay = el.style.display;
        el.style.display = 'none';
      });
  }

  _showUI() {
    document.querySelectorAll('[data-fp-display]').forEach(el => {
      el.style.display = el.dataset.fpDisplay ?? '';
      delete el.dataset.fpDisplay;
    });
  }

  // Costruisce l'overlay
  _buildOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.className = 'fp-overlay';

    // Bottone chiudi (stile tutorial + overrides per centro-alto)
    const btnClose = document.createElement('button');
    btnClose.className = 'tutorial-btn-close fp-btn-close';
    btnClose.innerHTML = '&#10005;';
    btnClose.title = 'Esci dalla prima persona (Esc)';
    btnClose.addEventListener('click', () => this.exit());
    this.overlay.appendChild(btnClose);

    if (this.mobile) {
      this._buildJoystick();
    } else {
      // Suggerimento comandi desktop
      const hint = document.createElement('div');
      hint.className = 'fp-hint';
      hint.innerHTML =
        'W/↑ avanti&nbsp;&nbsp;·&nbsp;&nbsp;S/↓ indietro&nbsp;&nbsp;·&nbsp;&nbsp;A/← ruota sinistra&nbsp;&nbsp;·&nbsp;&nbsp;D/→ ruota destra&nbsp;&nbsp;·&nbsp;&nbsp;<kbd style="background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.3);border-radius:4px;padding:1px 6px;font-family:monospace;font-size:11px;color:#fff">Esc</kbd>&nbsp;per uscire';
      this.overlay.appendChild(hint);
    }

    document.body.appendChild(this.overlay);
  }

  _buildJoystick() {
    // ── Joystick sinistra (movimento) ──
    const wrap = document.createElement('div');
    wrap.className = 'fp-joystick-wrap';

    const base = document.createElement('div');
    base.className = 'fp-joystick-base';

    const knob = document.createElement('div');
    knob.className = 'fp-joystick-knob';
    this._joystickKnob = knob;

    base.appendChild(knob);
    wrap.appendChild(base);
    this.overlay.appendChild(wrap);

    // Tocco iniziale: ancora il centro sul centro del base
    wrap.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      const r = base.getBoundingClientRect();
      this.joystick.active = true;
      this.joystick.id = t.identifier;
      this.joystick.cx = r.left + r.width  / 2;
      this.joystick.cy = r.top  + r.height / 2;
      this.joystick.dx = 0;
      this.joystick.dy = 0;
    }, { passive: false });

    wrap.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier !== this.joystick.id) continue;
        let dx = t.clientX - this.joystick.cx;
        let dy = t.clientY - this.joystick.cy;
        const dist = Math.hypot(dx, dy);
        if (dist > this.JOYSTICK_R) {
          const s = this.JOYSTICK_R / dist;
          dx *= s; dy *= s;
        }
        this.joystick.dx = dx / this.JOYSTICK_R;
        this.joystick.dy = dy / this.JOYSTICK_R;
        knob.style.transform =
          `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
      }
    }, { passive: false });

    const endJoystick = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier !== this.joystick.id) continue;
        this.joystick.active = false;
        this.joystick.dx = 0;
        this.joystick.dy = 0;
        knob.style.transform = 'translate(-50%, -50%)';
      }
    };
    wrap.addEventListener('touchend',    endJoystick, { passive: false });
    wrap.addEventListener('touchcancel', endJoystick, { passive: false });

    // Area destra (rotazione visuale)
    const lookArea = document.createElement('div');
    lookArea.className = 'fp-look-area';
    this.overlay.appendChild(lookArea);

    lookArea.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      this._lookTouch = { id: t.identifier, x: t.clientX, y: t.clientY };
    }, { passive: false });

    lookArea.addEventListener('touchmove', (e) => {
      e.preventDefault();
      if (!this._lookTouch) return;
      for (const t of e.changedTouches) {
        if (t.identifier !== this._lookTouch.id) continue;
        const dx = t.clientX - this._lookTouch.x;
        this._lookTouch.x = t.clientX;
        this._lookTouch.y = t.clientY;
        this.yaw -= dx * 0.005;
        this._applyRotation();
        window.requestRender?.();
      }
    }, { passive: false });

    lookArea.addEventListener('touchend',    () => { this._lookTouch = null; }, { passive: false });
    lookArea.addEventListener('touchcancel', () => { this._lookTouch = null; }, { passive: false });
  }

  // Tastiera
  _onKeyDown(e) {
    // Ignora se si sta scrivendo in un campo
    const tg = e.target;
    if (tg.tagName === 'INPUT' || tg.tagName === 'TEXTAREA' ||
        tg.tagName === 'SELECT' || tg.isContentEditable) return;

    // Blocca tutti gli altri listener (main.js, input-controller, ecc.)
    e.stopImmediatePropagation();

    if (e.key === 'Escape') { this.exit(); return; }

    switch (e.code) {
      case 'KeyW': case 'ArrowUp': this.keys.forward = true; e.preventDefault(); break;
      case 'KeyS': case 'ArrowDown': this.keys.backward = true; e.preventDefault(); break;
      case 'KeyA': case 'ArrowLeft': this.keys.left = true; e.preventDefault(); break;
      case 'KeyD': case 'ArrowRight': this.keys.right = true; e.preventDefault(); break;
    }
  }

  _onKeyUp(e) {
    switch (e.code) {
      case 'KeyW': case 'ArrowUp': this.keys.forward = false; break;
      case 'KeyS': case 'ArrowDown': this.keys.backward = false; break;
      case 'KeyA': case 'ArrowLeft': this.keys.left = false; break;
      case 'KeyD': case 'ArrowRight': this.keys.right = false; break;
    }
  }

  // Mouse look – solo yaw (sinistra/destra)
  _onMouseDown(e) {
    this._mouseDown = true;
    this._lastMX = e.clientX;
  }

  _onMouseMove(e) {
    if (!this._mouseDown) return;
    const dx = e.clientX - this._lastMX;
    this._lastMX = e.clientX;
    this.yaw -= dx * this.MOUSE_SENS;
    this._applyRotation();
    window.requestRender?.();
  }

  _onMouseUp() {
    this._mouseDown = false;
  }

  // Loop di movimento (RAF)
  _loop() {
    if (!this.isActive) return;
    this._rafId = requestAnimationFrame(this._loop);

    const anyKey = this.keys.forward || this.keys.backward ||
                   this.keys.left    || this.keys.right;
    const joyMoving = this.joystick.active &&
      (Math.abs(this.joystick.dx) > 0.04 || Math.abs(this.joystick.dy) > 0.04);

    if (!anyKey && !joyMoving) return; // nessun movimento → risparmia draw call

    // Rotazione con A/D (desktop)
    // A/← ruota a sinistra, D/→ ruota a destra
    const TURN_SPEED = 0.035; // rad/frame
    if (this.keys.left) this.yaw += TURN_SPEED;
    if (this.keys.right) this.yaw -= TURN_SPEED;
    if (this.keys.left || this.keys.right) this._applyRotation();

    // Vettori direzione
    // World forward = R_Y(yaw) × (0,0,-1) = (-sin(yaw), 0, -cos(yaw))
    // World right = R_Y(yaw) × (1,0,0) = ( cos(yaw), 0, -sin(yaw))
    this._fwd.set(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    this._right.set(Math.cos(this.yaw), 0, -Math.sin(this.yaw));
    this._vel.set(0, 0, 0);

    if (this.keys.forward) this._vel.addScaledVector(this._fwd, this.MOVE_SPEED);
    if (this.keys.backward) this._vel.addScaledVector(this._fwd, -this.MOVE_SPEED);

    if (joyMoving) {
      // Stesso principio del desktop: asse Y = avanti/indietro, asse X = ruota (yaw)
      this._vel.addScaledVector(this._fwd, -this.joystick.dy * this.MOVE_SPEED);
      const JOY_TURN = 0.035; // rad/frame, uguale a TURN_SPEED da tastiera
      this.yaw -= this.joystick.dx * JOY_TURN;
      this._applyRotation();
    }

    this.camera.position.add(this._vel);
    this.camera.position.y = this.EYE_HEIGHT; // mantiene sempre all'altezza occhi

    window.requestRender?.();
  }

  // Applica rotazione (yaw + pitch) alla camera
  _applyRotation() {
    // Ordine YXZ: prima yaw attorno a Y (girar la testa), poi pitch attorno a X locale
    this._euler.set(this.pitch, this.yaw, 0, 'YXZ');
    this._quat.setFromEuler(this._euler);
    this.camera.quaternion.copy(this._quat);
  }
}

export default FirstPersonController;